import { Paper } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { SalesPersonWitHChallanDataJson } from "../../DynamicFormsJson/MastersJSON/SalesPersonWithChallanData";
import ButtonComponent from "../../components/Comman/ButtonComponent";
import SearchBoxCompo from "../../components/Comman/SearchBoxCompo";
import TypographyCompo from "../../components/Comman/TypographyCompo";
import DynamicMainScreen from "../../components/dynamicscreens/DynamicMainScreen";
import {
    darkBlueShade,
    greenShade,
    slightRed,
    white,
    whiteShadeColor,
} from "../../config/ColorObj";
import ChallanDataPopUp from "./components/ChallanDataPopUp";
import { ChevronIcon, ExternalLinkIcon } from "./components/SvgIcons";
import {
    formatINR,
    getInitials,
    getPerfMeta,
    PERF_COLORS,
} from "./helpers/Helpers";
import { styles } from "./styles/SalesPersonWithChallanData";

// ─── Static / Mock Data (move outside component to avoid re-creation) ─────────
const API_RESPONSE = {
    salesTeamList: [
        { id: 1, name: "Rahul Patil" },
        { id: 2, name: "Amit Sharma" },
        { id: 3, name: "Sneha Joshi" },
    ],
    challanList: [
        { salesId: 1, challanId: "1", challanName: "Challan 1", challanDate: "10-04-2026", challanQty: 50, generatedBillQty: 5, sellQty: 30 },
        { salesId: 1, challanId: "2", challanName: "Challan 2", challanDate: "11-04-2026", challanQty: 30, generatedBillQty: 2, sellQty: 15 },
        { salesId: 2, challanId: "3", challanName: "Challan 3", challanDate: "10-04-2026", challanQty: 40, generatedBillQty: 3, sellQty: 25 },
        { salesId: 2, challanId: "4", challanName: "Challan 4", challanDate: "12-04-2026", challanQty: 20, generatedBillQty: 1, sellQty: 10 },
        { salesId: 3, challanId: "5", challanName: "Challan 5", challanDate: "11-04-2026", challanQty: 60, generatedBillQty: 8, sellQty: 45 },
    ],
};

// ─── Sub-components (memoized to prevent unnecessary re-renders) ──────────────

const Avatar = React.memo(({ name }) => (
    <div style={styles.avatar}>{getInitials(name)}</div>
));
Avatar.displayName = "Avatar";

const PerfPill = React.memo(({ pct }) => {
    const { label, colorClass } = getPerfMeta(pct);
    return (
        <TypographyCompo style={{ ...styles.perfPill, ...styles.perfPillVariants[colorClass] }}>
            {label} · {pct}%
        </TypographyCompo>
    );
});
PerfPill.displayName = "PerfPill";

const ProgressBar = React.memo(({ pct, color }) => (
    <div style={styles.progWrap}>
        <div style={styles.progTrack}>
            <div style={{ ...styles.progFill, width: `${pct}%`, background: color }} />
        </div>
        <TypographyCompo style={{ ...styles.progPct, color }}>{pct}%</TypographyCompo>
    </div>
));
ProgressBar.displayName = "ProgressBar";

const StatusDot = React.memo(({ color }) => (
    <TypographyCompo style={{ ...styles.statusDot, background: color }} />
));
StatusDot.displayName = "StatusDot";

// ─── Challan Row (isolated to prevent full sub-table re-render) ───────────────
const ChallanRow = React.memo(({ challan, salesName, onChallanClick }) => {
    const pct = challan.challanQty
        ? Math.round((challan.sellQty / challan.challanQty) * 100)
        : 0;
    const { colorClass } = getPerfMeta(pct);
    const color = PERF_COLORS[colorClass];
    const remaining = challan.challanQty - challan.sellQty;

    const handleMouseEnter = useCallback((e) => {
        e.currentTarget.style.background = "var(--sub-row-hover, #f8fafc)";
        e.currentTarget.querySelector("[data-link]").style.color = darkBlueShade;
        e.currentTarget.querySelector("[data-ext]").style.opacity = "1";
    }, []);

    const handleMouseLeave = useCallback((e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.querySelector("[data-link]").style.color = "inherit";
        e.currentTarget.querySelector("[data-ext]").style.opacity = "0";
    }, []);

    const handleClick = useCallback(() => {
        onChallanClick(challan.challanId, challan.challanName, salesName);
    }, [onChallanClick, challan.challanId, challan.challanName, salesName]);

    return (
        <tr
            style={styles.subRow}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <td style={styles.subTd} onClick={handleClick}>
                <TypographyCompo style={styles.challanLink} data-link>
                    <StatusDot color={color} />
                    {challan.challanName}
                    <TypographyCompo data-ext style={{ opacity: 0, transition: "opacity .15s" }}>
                        <ExternalLinkIcon />
                    </TypographyCompo>
                </TypographyCompo>
            </td>
            <td style={{ ...styles.subTd, ...styles.mutedText }}>{challan.challanDate}</td>
            <td style={{ ...styles.subTd, textAlign: "right" }}>{challan.challanQty}</td>
            <td style={{ ...styles.subTd, textAlign: "right", color: greenShade, fontWeight: 500 }}>
                {challan.sellQty}
            </td>
            <td style={{ ...styles.subTd, textAlign: "right", color: slightRed }}>
                {remaining}
            </td>
            <td style={styles.subTd}>
                <ProgressBar pct={pct} color={color} />
            </td>
        </tr>
    );
});
ChallanRow.displayName = "ChallanRow";

// ─── Challan Sub-Table ────────────────────────────────────────────────────────
const ChallanSubTable = React.memo(({ challans, salesName, onChallanClick }) => {
    if (!challans.length) {
        return (
            <TypographyCompo style={styles.noData}>No challans assigned.</TypographyCompo>
        );
    }

    return (
        <div style={styles.subTableWrap}>
            <table style={styles.subTable}>
                <thead>
                    <tr>
                        <th style={styles.subTh}>Challan</th>
                        <th style={styles.subTh}>Date</th>
                        <th style={{ ...styles.subTh, textAlign: "right" }}>Challan Qty</th>
                        <th style={{ ...styles.subTh, textAlign: "right" }}>Sold Qty</th>
                        <th style={{ ...styles.subTh, textAlign: "right" }}>Remaining</th>
                        <th style={{ ...styles.subTh, minWidth: 130 }}>Progress</th>
                    </tr>
                </thead>
                <tbody>
                    {challans.map((c) => (
                        <ChallanRow
                            key={c.id ?? c.challanId}
                            challan={c}
                            salesName={salesName}
                            onChallanClick={onChallanClick}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
});
ChallanSubTable.displayName = "ChallanSubTable";

// ─── Expand Panel ─────────────────────────────────────────────────────────────
const ExpandPanel = React.memo(({ isOpen, challans, salesName, onChallanClick }) => (
    <tr>
        <td colSpan={10} style={{ padding: 0, border: "none" }}>
            <div
                style={{
                    ...styles.expandPanel,
                    maxHeight: isOpen ? "600px" : "0px",
                    opacity: isOpen ? 1 : 0,
                }}
            >
                <div style={styles.expandBody}>
                    <ChallanSubTable
                        challans={challans}
                        salesName={salesName}
                        onChallanClick={onChallanClick}
                    />
                </div>
            </div>
        </td>
    </tr>
));
ExpandPanel.displayName = "ExpandPanel";

// ─── Sales Person Row ─────────────────────────────────────────────────────────
const SalesPersonRow = React.memo(({ salesperson, index, isOpen, onToggle, onChallanClick }) => {
    const { s, challans, totals, pct, generatedBill, collectedAmt } = salesperson;

    const handleMouseEnter = useCallback((e) => {
        Array.from(e.currentTarget.cells).forEach(
            (td) => (td.style.background = whiteShadeColor)
        );
    }, []);

    const handleMouseLeave = useCallback((e) => {
        Array.from(e.currentTarget.cells).forEach(
            (td) => (td.style.background = white)
        );
    }, []);

    const handleRowClick = useCallback(() => onToggle(s.id), [onToggle, s.id]);

    const handleChevronClick = useCallback(
        (e) => {
            e.stopPropagation();
            onToggle(s.id);
        },
        [onToggle, s.id]
    );

    return (
        <>
            <tr
                style={styles.mainRow}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleRowClick}
            >
                <td style={{ ...styles.td, textAlign: "center", ...styles.mutedText, fontSize: 12 }}>
                    {index + 1}
                </td>
                <td style={styles.td}>
                    <div style={styles.nameCell}>
                        <Avatar name={s.name} />
                        <TypographyCompo style={styles.salesName}>{s.name}</TypographyCompo>
                    </div>
                </td>
                <td style={{ ...styles.td, textAlign: "right", ...styles.mutedText }}>
                    {challans.length}
                </td>
                <td style={{ ...styles.td, textAlign: "right" }}>{totals.challan}</td>
                <td style={{ ...styles.td, textAlign: "right" }}>{totals.generatedBillQty}</td>
                <td style={{ ...styles.td, textAlign: "right" }}>{totals.sell}</td>
                <td style={{ ...styles.td, textAlign: "right" }}>{formatINR(generatedBill)}</td>
                <td style={{ ...styles.td, textAlign: "right" }}>
                    <PerfPill pct={pct} />
                </td>
                <td style={{ ...styles.td, textAlign: "right" }}>{formatINR(collectedAmt)}</td>
                <td style={{ ...styles.td, textAlign: "center" }}>
                    <ButtonComponent style={styles.chevronBtn} onClick={handleChevronClick}>
                        <ChevronIcon open={isOpen} />
                    </ButtonComponent>
                </td>
            </tr>

            <ExpandPanel
                isOpen={isOpen}
                challans={challans}
                salesName={s.name}
                onChallanClick={onChallanClick}
            />
        </>
    );
});
SalesPersonRow.displayName = "SalesPersonRow";

// ─── Main Component ───────────────────────────────────────────────────────────
const SalesPersonWithChallanData = ({ onChallanClick }) => {
    const [challanPopUpFlag, setChallanPopUpFlag] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [search, setSearch] = useState("");

    const { salesTeamList = [], challanList = [] } = API_RESPONSE;

    // Stable toggle handler
    const toggle = useCallback(
        (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] })),
        []
    );

    // Stable challan click handler
    const handleChallanClick = useCallback(
        (challanId, challanName, salesName) => {
            if (onChallanClick) {
                onChallanClick({ challanId, challanName, salesName });
                return;
            }
            setChallanPopUpFlag(true);
        },
        [onChallanClick]
    );

    // Stable popup close handler
    const handlePopUpClose = useCallback(() => {
        setChallanPopUpFlag(false);
    }, []);

    // Stable search handler (used by DynamicMainScreen)
    const onSearchData = useCallback((data) => {
        console.log("data", data);
    }, []);

    // Derived matrix — recomputes only when search/data changes
    const matrix = useMemo(() => {
        const lowerSearch = search.toLowerCase();
        return salesTeamList
            .filter((s) => s.name.toLowerCase().includes(lowerSearch))
            .map((s) => {
                const challans = challanList.filter((c) => c.salesId === s.id);
                const totals = challans.reduce(
                    (acc, c) => {
                        acc.challan += c.challanQty;
                        acc.sell += c.sellQty;
                        acc.generatedBillQty += c.generatedBillQty;
                        return acc;
                    },
                    { challan: 0, sell: 0, generatedBillQty: 0 }
                );
                const pct = totals.challan
                    ? Math.round((totals.sell / totals.challan) * 100)
                    : 0;
                return {
                    s,
                    challans,
                    totals,
                    pct,
                    generatedBill: totals.sell * 10,
                    collectedAmt: totals.sell * 8,
                };
            });
    }, [search, salesTeamList, challanList]);

    // Table content — only re-renders when matrix or expanded state changes
    const tableContent = useMemo(() => {
        if (matrix.length === 0) {
            return (
                <tr>
                    <td colSpan={9} style={styles.emptyCell}>
                        No records found.
                    </td>
                </tr>
            );
        }

        return matrix.map((salesperson, i) => (
            <SalesPersonRow
                key={salesperson.s.id}
                salesperson={salesperson}
                index={i}
                isOpen={!!expanded[salesperson.s.id]}
                onToggle={toggle}
                onChallanClick={handleChallanClick}
            />
        ));
    }, [matrix, expanded, toggle, handleChallanClick]);

    return (
        <Paper sx={{ p: 2, borderRadius: 0 }} elevation={0}>
            <DynamicMainScreen
                dynamicMasterData={{}}
                maxDateValid={true}
                showSearchBox={false}
                onSearchData={onSearchData}
                screenTitle={SalesPersonWitHChallanDataJson.screenTitle}
                fieldMeta={[]}
                searchList={SalesPersonWitHChallanDataJson.searchList}
                showPdfDownload={false}
                showExcelDownload={false}
                showExtension={false}
                pdfFileName={false}
                showAddButton={false}
                tableColumnsToFilter={[]}
                tableColumns={[]}
                tableData={[]}
                getTableData={onSearchData}
                callApi={false}
                getTableDataById={false}
                apiBaseURL={SalesPersonWitHChallanDataJson.apiBaseURL}
                baseIdColumn={SalesPersonWitHChallanDataJson.baseIdColumn}
                isNote={false}
                showDeleteIcon={false}
                showViewButtonInTable={false}
            />

            {/* Search */}
            <SearchBoxCompo
                value={search}
                onChange={setSearch}
                placeholder="Search sales person..."
                style={{ marginTop: -30, marginBottom: 4 }}
            />

            {/* Table */}
            <div style={styles.tableWrapper}>
                <table style={styles.table}>
                    <thead style={styles.thead}>
                        <tr>
                            <th style={{ ...styles.th, width: 36 }}>#</th>
                            <th style={styles.th}>Sales Person</th>
                            <th style={{ ...styles.th, textAlign: "right" }}>Challans</th>
                            <th style={{ ...styles.th, textAlign: "right" }}>Challan Qty</th>
                            <th style={{ ...styles.th, textAlign: "right" }}>Generated Bill</th>
                            <th style={{ ...styles.th, textAlign: "right" }}>Sell Qty</th>
                            <th style={{ ...styles.th, textAlign: "right" }}>Generated Bill Amount</th>
                            <th style={{ ...styles.th, textAlign: "right" }}>Performance</th>
                            <th style={{ ...styles.th, textAlign: "right" }}>Collected Amt</th>
                            <th style={{ ...styles.th, width: 40 }} />
                        </tr>
                    </thead>
                    <tbody>{tableContent}</tbody>
                </table>
            </div>

            {challanPopUpFlag && (
                <ChallanDataPopUp open={challanPopUpFlag} handleCloses={handlePopUpClose} />
            )}
        </Paper>
    );
};

export default SalesPersonWithChallanData;